import { createTheme, type Theme } from '@mui/material/styles';
import { palette, radius, spacing, typography, type ThemeMode } from './tokens';

export function createChatTheme(mode: ThemeMode): Theme {
  const p = palette[mode];

  return createTheme({
    palette: {
      mode,
      primary: { main: p.accent.primary, dark: p.accent.hover, contrastText: '#FFFFFF' },
      success: { main: p.semantic.success },
      warning: { main: p.semantic.warning },
      error: { main: p.semantic.error },
      background: { default: p.bg.default, paper: p.bg.surface },
      text: { primary: p.text.primary, secondary: p.text.secondary, disabled: p.text.tertiary },
      divider: p.border.subtle,
    },
    shape: { borderRadius: radius.md },
    typography: {
      fontFamily: typography.fontFamilySans,
      fontSize: 14,
      htmlFontSize: 16,
      h1: { fontSize: typography.sizes.h1, fontWeight: 600, lineHeight: typography.lineHeights.h1 },
      h2: {
        fontSize: typography.sizes.h2,
        fontWeight: 600,
        lineHeight: typography.lineHeights.h2,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      },
      body1: {
        fontSize: typography.sizes.body,
        lineHeight: typography.lineHeights.body,
      },
      body2: {
        fontSize: typography.sizes.bodySmall,
        lineHeight: typography.lineHeights.bodySmall,
      },
      caption: {
        fontSize: typography.sizes.caption,
        lineHeight: typography.lineHeights.caption,
      },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: typography.fontFamilySans,
            backgroundColor: p.bg.default,
            color: p.text.primary,
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: p.bg.surface,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: false },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: radius.md,
            transition: 'background-color 180ms ease-out, color 180ms ease-out',
          },
          containedPrimary: {
            backgroundColor: p.accent.primary,
            '&:hover': { backgroundColor: p.accent.hover },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            transition: 'background-color 180ms ease-out, color 180ms ease-out',
            '&:hover': { backgroundColor: p.bg.subtle },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: radius.sm,
          },
          outlined: {
            borderColor: p.border.default,
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: radius.md,
              backgroundColor: p.bg.surface,
              '& fieldset': { borderColor: p.border.default },
              '&:hover fieldset': { borderColor: p.text.tertiary },
              '&.Mui-focused fieldset': { borderColor: p.accent.primary, borderWidth: 1 },
            },
          },
        },
      },
      MuiSwitch: {
        defaultProps: { size: 'small' },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: typography.sizes.caption,
            backgroundColor: mode === 'light' ? '#1F2024' : '#26282F',
            borderRadius: radius.sm,
            padding: `${spacing[1]}px ${spacing[2]}px`,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 36 },
          indicator: { height: 2, backgroundColor: p.accent.primary },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minHeight: 36,
            padding: `${spacing[2]}px ${spacing[3]}px`,
            fontSize: typography.sizes.bodySmall,
            fontWeight: 500,
            color: p.text.secondary,
            '&.Mui-selected': { color: p.text.primary },
          },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: p.border.subtle } },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 4, borderRadius: radius.sm, backgroundColor: p.bg.subtle },
          bar: { borderRadius: radius.sm },
        },
      },
    },
  });
}
